/**
 * ViewToggle - Segment control for Karte | Liste view
 * Supports mapOnly mode for just showing a Map button
 */
import { memo } from 'react';
import { Map, List, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';

type ViewType = 'list' | 'grid' | 'map';

interface ViewToggleProps {
  value: ViewType;
  onChange: (value: ViewType) => void;
  showMap?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  /** Icons only mode for compact mobile display */
  iconOnly?: boolean;
  /** Show only Map button (toggles between list and map) */
  mapOnly?: boolean;
}

export const ViewToggle = memo(function ViewToggle({
  value,
  onChange,
  showMap = false,
  className,
  size = 'md',
  iconOnly = false,
  mapOnly = false
}: ViewToggleProps) {
  // Map-only mode: Single button that toggles between list and map
  if (mapOnly) {
    const isMapActive = value === 'map';
    return (
      <button
        onClick={() => onChange(isMapActive ? 'list' : 'map')}
        className={cn(
          "flex items-center justify-center rounded-xl transition-colors duration-200",
          isMapActive 
            ? "bg-[#009544] text-white shadow-sm" 
            : "bg-white border border-gray-200 text-gray-600 hover:border-[#009544]/30 hover:text-[#009544]",
          className
        )}
        aria-label={isMapActive ? "Zur Liste" : "Karte anzeigen"}
        title={isMapActive ? "Zur Liste" : "Karte anzeigen"}
      >
        <Map className="w-5 h-5" />
      </button>
    );
  }
  
  const options = [
    { value: 'list' as const, label: 'Liste', icon: List },
    { value: 'grid' as const, label: 'Karten', icon: LayoutGrid },
    ...(showMap ? [{ value: 'map' as const, label: 'Karte', icon: Map }] : [])
  ];
  
  return (
    <div 
      className={cn(
        "inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5",
        className
      )}
      role="tablist"
    >
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = value === option.value;
        
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-center justify-center rounded-md transition-colors duration-200",
              iconOnly 
                ? "p-2" 
                : size === 'sm' ? "px-2.5 py-1.5 text-xs gap-1.5" : "px-3 py-2 text-sm gap-1.5",
              isActive 
                ? "bg-white text-[#009544] font-medium shadow-sm" 
                : "text-gray-600 hover:text-gray-900"
            )}
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${option.value}`}
            aria-label={iconOnly ? option.label : undefined}
          >
            <Icon className={cn(
              iconOnly ? "w-4 h-4" : size === 'sm' ? "w-3.5 h-3.5" : "w-4 h-4"
            )} />
            {!iconOnly && <span>{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
});
